import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.scss';
import {
  vimeoParser,
  youtubeParser,
  booleanToBit,
  calculateHeight,
} from '../../../../src/web/utils/videoUtils';
import { useWindowResizer } from '../../../../src/web/utils/useWindowResizer';

const Video = (props) => {
  const {
    autoPlay,
    controls,
    loop,
    mute: muteProp,
    url,
    videoSource,
    title,
    altText,
    isBuilderView,
    contentHeight,
    contentWidth,
    isSectionBackground,
  } = props;
  let videoUrl = url;
  let videoId;
  const mute = autoPlay ? true : muteProp;
  let content = null;

  const parentRef = useRef(null);
  const { width = 100 } = useWindowResizer({ ref: parentRef });

  const convertToQueryParams = params => Object.keys(params)
    .map(
      key => `${key}=${
        typeof params[key] === 'boolean'
          ? booleanToBit(params[key])
          : params[key]
      }`,
    )
    .join('&');

  const widgetHeight = useMemo(
    () => calculateHeight(contentWidth, contentHeight, width),
    [width],
  );


  switch (videoSource) {
    case 'youtube':
      videoId = youtubeParser(url);
      videoUrl = `https://www.youtube.com/embed/${videoId}?${convertToQueryParams(
        {
          autoplay: autoPlay,
          loop,
          mute,
          controls,
          playlist: videoId,
        },
      )}`;
      break;
    case 'vimeo':
      videoId = vimeoParser(url);
      videoUrl = `https://player.vimeo.com/video/${videoId}?${convertToQueryParams(
        {
          autoplay: autoPlay,
          loop,
          muted: mute,
          controls,
          autoPause: autoPlay ? '0' : '',
        },
      )}`;
      break;
    default:
      // Case of Normal Video Element
      break;
  }
  switch (videoSource) {
    case 'youtube':
    case 'vimeo':
      content = (
        <iframe
          title={title}
          alt={altText}
          src={videoUrl}
          className={styles.iframeContainer}
          frameborder="0"
          allowFullScreen
          allow="autoplay; fullscreen; picture-in-picture"
        />
      );
      break;
    default: {
      const videoMissing = isBuilderView && !url;
      content = (
        <div
          style={
            videoMissing
              ? {
                height: '100%',
                width: '100%',
                backgroundColor: '#000',
                color: '#fff',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
              }
              : {}
          }
          className={videoMissing ? styles.overlay : ''}
        >
          {videoMissing && (
            <p>Click here to edit this widget and add your video url</p>
          )}
        </div>
      );
      break;
    }
  }

  if (isSectionBackground) {
    return (
        <div
          id="parent-video-container"
          className={styles.parentContainer}
          ref={parentRef}
        >
          <div className={styles.videoIframeContainer}>{content}</div>
        </div>
    );
  }

  return (
    <div
      id="parent-video-container"
      className={styles.parentContainer}
      ref={parentRef}
    >
      <div style={{ height: widgetHeight }}>{content}</div>
    </div>
  );
};

Video.propTypes = {
  url: PropTypes.string.isRequired,
  autoPlay: PropTypes.bool,
  loop: PropTypes.bool,
  mute: PropTypes.bool,
  controls: PropTypes.bool,
  videoSource: PropTypes.string.isRequired,
  altText: PropTypes.string,
  title: PropTypes.string,
  isBuilderView: PropTypes.bool,
  contentHeight: PropTypes.number,
  contentWidth: PropTypes.number,
};
Video.defaultProps = {
  isBuilderView: false,
  autoPlay: false,
  loop: false,
  mute: false,
  controls: false,
  altText: 'Video Element',
  title: 'Video Element',
  contentHeight: 9,
  contentWidth: 16,
};

export default Video;
